import React, { useEffect, useState } from 'react';
import { createInstance, OptimizelyProvider as OptimizelySDKProvider } from '@optimizely/react-sdk';
import { getCountry } from 'i18n/i18nUtils';
import { isKiosk, isMobile, isProduction } from 'utils/platform';
import { CookieConsentLevels, useCookieConsent } from 'contexts/cookieConsent';
import { getRange } from 'utils/queryUtils';
import { getUserId, timeout } from 'optimizely/utils';

type Props = {
  children: React.ReactNode;
};

const SDKKey = isProduction() ? '73ANu7N6D94s8inWeguwy' : 'WMDQRZCGXdYUMtzRVU2Hj';

export const optimizelyInstance = createInstance({
  sdkKey: SDKKey,
  eventBatchSize: 10,
  eventFlushInterval: 1000
});

type CookieConsentStatus = 'pending' | 'no-consent' | 'consent';

const OptimizelyProvider = ({ children }: Props) => {
  const userId = getUserId();
  const countryCode = getCountry().toLowerCase();
  const { hasCookieConsent } = useCookieConsent();
  const [sdkReady, setSdkReady] = useState(optimizelyInstance.isReady());
  const [hasAnalyticsCookieConsent, setHasAnalyticsCookieConsent] = useState<CookieConsentStatus>(
    'pending'
  );

  useEffect(() => {
    hasCookieConsent(CookieConsentLevels.ANALYTICAL).then(consent =>
      setHasAnalyticsCookieConsent(consent ? 'consent' : 'no-consent')
    );
  }, [hasCookieConsent]);

  useEffect(() => {
    optimizelyInstance.onReady({ timeout }).then(() => setSdkReady(true));
  }, []);

  if (hasAnalyticsCookieConsent === 'pending' || !sdkReady) {
    return null;
  }

  const user =
    hasAnalyticsCookieConsent === 'consent'
      ? {
          id: userId,
          attributes: {
            countryCode: countryCode,
            range: getRange(),
            environment: isProduction() ? 'production' : 'development',
            isKiosk: isKiosk(),
            isMobile: isMobile()
          }
        }
      : undefined;

  return (
    <OptimizelySDKProvider optimizely={optimizelyInstance} user={user} timeout={timeout}>
      {children}
    </OptimizelySDKProvider>
  );
};

export default OptimizelyProvider;
